import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { darkTheme } from '../../lib/tokens';
import { ModalShell, ModalActions } from '../../components/PromptDialog';
import { TableScroll } from '../../components/TableScroll';
import { createPost, fetchAssets, socialKeys } from './api';
import { EmptyHint, KuerzelChip, dauer, kanalKurz, kanalName, tagKurz } from './bausteine';
import { PUBLISH_CHANNELS, type BestandsAsset, type Channel } from './types';
import { useCanWrite } from '../auth/permissions';

const t = darkTheme.colors;

/**
 * Bestand (CUR-326) — der Schirm, der die Frage beantwortet, an der der
 * Kalender aus CUR-307 gescheitert ist: **was ist fertig, läuft aber nirgends?**
 *
 * Der Plan vom 13.08. hat sieben fertige Stücke nicht gesehen, weil der Bestand
 * nirgends maschinell abfragbar war. Der Umschalter „nur unverplante" steht
 * deshalb als Erstes über der Tabelle und nicht in einem Filtermenü — er ist
 * der wahre Zweck dieses Schirms, nicht eine seiner Optionen.
 */
export function BestandPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const nurOffene = params.get('offen') === '1';
  const vorgabeStream = params.get('stream');
  const vorgabeSlot = params.get('slot');
  const vorgabeKanal = params.get('channel') as Channel | null;

  const [terminFuer, setTerminFuer] = useState<BestandsAsset | null>(null);
  // Der Bestand bleibt lesbar; nur das Terminieren entfaellt (CUR-995).
  const canWrite = useCanWrite();

  const assets = useQuery({
    queryKey: socialKeys.assets(nurOffene),
    queryFn: () => fetchAssets(nurOffene),
  });

  const gefiltert = (assets.data?.assets ?? []).filter(
    (a) => !vorgabeStream || a.stream === vorgabeStream,
  );

  return (
    <div className="mx-auto max-w-[1400px] p-4 lg:p-6">
      <header className="mb-4">
        <h1 className="text-xl font-bold" style={{ color: t.textPrimary }}>Bestand</h1>
        <p className="text-sm" style={{ color: t.textTertiary }}>
          {assets.data ? `${gefiltert.length} Asset(s)` : '…'}
          {vorgabeStream ? ` · gefiltert auf ${vorgabeStream}` : ''}
        </p>
      </header>

      {vorgabeSlot && vorgabeKanal && (
        <p className="mb-4 rounded-md border p-3 text-sm"
          style={{ borderColor: t.accent, backgroundColor: t.accentSubtle, color: t.accentLight }}>
          Freier Slot am {tagKurz(vorgabeSlot)} ({vorgabeKanal}) — eine Folge auswählen und „Termin anlegen".
        </p>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <label className="flex min-h-[44px] cursor-pointer items-center gap-2 text-sm font-semibold"
          style={{ color: nurOffene ? t.accentLight : t.textSecondary }}>
          <input
            type="checkbox"
            checked={nurOffene}
            onChange={(e) => {
              const next = new URLSearchParams(params);
              if (e.target.checked) next.set('offen', '1');
              else next.delete('offen');
              setParams(next, { replace: true });
            }}
          />
          nur unverplante
        </label>
        {vorgabeStream && (
          <button type="button"
            onClick={() => {
              const next = new URLSearchParams(params);
              next.delete('stream');
              setParams(next, { replace: true });
            }}
            className="min-h-[44px] rounded-md px-3 text-sm"
            style={{ backgroundColor: t.bgElevated, color: t.textSecondary }}>
            Strang-Filter aufheben
          </button>
        )}
      </div>

      {assets.isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 8 }, (_, i) => (
            <div key={i} className="h-12 animate-pulse rounded-md" style={{ backgroundColor: t.bgSurface }} />
          ))}
        </div>
      )}

      {assets.data && gefiltert.length === 0 && (
        nurOffene ? (
          // Ein Nullergebnis ist hier die GUTE Nachricht — also wird sie auch
          // so formuliert. „Keine Treffer" läse sich wie ein Fehler.
          <div className="rounded-md border p-6 text-sm"
            style={{ borderColor: t.borderSubtle, backgroundColor: t.bgSurface, color: t.success }}>
            Alles Fertige ist eingeplant.
          </div>
        ) : (
          <EmptyHint
            title="Der Bestand ist leer. Er wird vom Sync-Lauf gefüllt:"
            command="ADMIN_TOKEN=… node scripts/social/sync-assets.mjs"
          />
        )
      )}

      {assets.data && gefiltert.length > 0 && (
        <TableScroll minWidthClass="min-w-[880px]">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ color: t.textTertiary }}>
                {['', 'Titel', 'Strang', 'Format', 'Dauer', 'Terminiert', ''].map((h, i) => (
                  <th key={i} className="px-3 py-2 text-left text-xs font-semibold uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {gefiltert.map((a) => (
                <tr key={a.id} style={{ borderTop: `1px solid ${t.borderSubtle}` }}>
                  <td className="px-3 py-2">
                    {a.thumbnailUrl ? (
                      <img src={a.thumbnailUrl} alt="" className="h-8 w-14 rounded object-cover" />
                    ) : (
                      <div className="flex h-8 w-14 items-center justify-center rounded text-[9px]"
                        style={{ backgroundColor: t.bgElevated, color: t.textTertiary }}>
                        {a.stream.slice(0, 6)}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2" style={{ color: t.textPrimary }}>
                    {/* Kürzel vor dem Titel wie im Kalender; die Kennung bleibt grau dahinter (CUR-2287). */}
                    <span className="inline-flex flex-wrap items-center gap-2">
                      <KuerzelChip kuerzel={a.kuerzel} ordner={a.ordner} />
                      <span>{a.title}</span>
                      <span className="text-xs" style={{ color: t.textTertiary }}>{a.slug}</span>
                    </span>
                  </td>
                  <td className="px-3 py-2" style={{ color: t.textSecondary }}>{a.stream}</td>
                  <td className="px-3 py-2" style={{ color: t.textSecondary }}>{a.aspectRatio ?? '—'}</td>
                  <td className="px-3 py-2 tabular-nums" style={{ color: t.textSecondary }}>{dauer(a.durationSec)}</td>
                  <td className="px-3 py-2">
                    {a.scheduledOn.length === 0 ? (
                      // Gedankenstrich, nicht leere Zelle: leer liest sich wie
                      // „noch nicht geladen".
                      <span style={{ color: t.textTertiary }}>—</span>
                    ) : (
                      <span className="flex flex-wrap gap-2">
                        {a.scheduledOn.map((s) => (
                          <span key={`${s.channel}-${s.date ?? 'ohne'}`} className="text-xs" style={{ color: t.textSecondary }}>
                            {kanalKurz(s.channel)} {s.date ? tagKurz(s.date) : 'ohne Termin'}
                          </span>
                        ))}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {canWrite && (
                      <button type="button" onClick={() => setTerminFuer(a)}
                        className="min-h-[44px] rounded-md px-3 text-xs font-semibold"
                        style={{ backgroundColor: t.bgElevated, color: t.accentLight }}>
                        Termin anlegen
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}

      {terminFuer && (
        <TerminModal
          asset={terminFuer}
          vorgabeDatum={vorgabeSlot}
          vorgabeKanal={vorgabeKanal}
          onClose={() => setTerminFuer(null)}
        />
      )}
    </div>
  );
}

function TerminModal({
  asset, vorgabeDatum, vorgabeKanal, onClose,
}: {
  asset: BestandsAsset;
  vorgabeDatum: string | null;
  vorgabeKanal: Channel | null;
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const navigate = useNavigate();
  // Vorbelegt auf den ersten Kanal, für den es überhaupt eine Fassung gibt —
  // ein Termin ohne Fassung wäre ein Cockpit ohne Text, und das Backend weist
  // ihn ohnehin ab.
  // `APP` ist Fachreview und nie ein Termin — der Filter macht aus der Liste
  // zugleich den Typ, den `createPost` annimmt.
  const moeglich = asset.channels.filter((c): c is Channel => (PUBLISH_CHANNELS as readonly string[]).includes(c));
  const [kanal, setKanal] = useState<Channel>(
    (vorgabeKanal && moeglich.includes(vorgabeKanal) ? vorgabeKanal : moeglich[0]) ?? 'YOUTUBE',
  );
  const [datum, setDatum] = useState(vorgabeDatum ?? '');
  const [fehler, setFehler] = useState<string | null>(null);

  const anlegen = useMutation({
    mutationFn: () => createPost({ assetId: asset.id, channel: kanal, scheduledFor: datum }),
    onSuccess: (post) => {
      void qc.invalidateQueries({ queryKey: ['social'] });
      navigate(`/social/post/${post.id}`);
    },
    onError: (e: Error) => setFehler(e.message),
  });

  return (
    <ModalShell
      title={`Termin für ${asset.slug}`}
      onSubmit={(e) => {
        e.preventDefault();
        if (!datum || moeglich.length === 0) return;
        anlegen.mutate();
      }}
    >
      {moeglich.length === 0 ? (
        <p className="text-sm" style={{ color: t.warning }}>
          Für dieses Asset gibt es keine Kanalfassung — erst das Manifest ergänzen und neu einspielen.
        </p>
      ) : (
        <>
          <label className="mb-3 block text-sm" style={{ color: t.textSecondary }}>
            Kanal
            <select value={kanal} onChange={(e) => setKanal(e.target.value as Channel)}
              className="mt-1 block min-h-[44px] w-full rounded-md px-3"
              style={{ backgroundColor: t.bgElevated, color: t.textPrimary }}>
              {moeglich.map((c) => <option key={c} value={c}>{kanalName(c)}</option>)}
            </select>
          </label>
          <label className="mb-3 block text-sm" style={{ color: t.textSecondary }}>
            Datum
            <input type="date" value={datum} onChange={(e) => setDatum(e.target.value)}
              className="mt-1 block min-h-[44px] w-full rounded-md px-3"
              style={{ backgroundColor: t.bgElevated, color: t.textPrimary }} />
          </label>
          {fehler && <p className="mb-3 text-sm" style={{ color: t.danger }}>{fehler}</p>}
        </>
      )}
      <ModalActions
        onCancel={onClose}
        confirmLabel="Anlegen"
        disabled={!datum || moeglich.length === 0 || anlegen.isPending}
      />
    </ModalShell>
  );
}
